import { formatCurrencyBR } from '@/utils/formatUtils'

export function formatCurrency(value: number): string {
  return formatCurrencyBR(value || 0)
}

/** True when formatCurrencyCompact abbreviates, so the exact value is worth showing */
export function isCurrencyAbbreviated(value: number): boolean {
  return Math.abs(value || 0) >= 10_000
}

/** KPI cards need short values: a full "R$ 1.487.500,00" breaks the card layout. */
export function formatCurrencyCompact(value: number): string {
  const amount = value || 0
  const abs = Math.abs(amount)

  if (abs >= 1_000_000) {
    return `R$ ${(amount / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mi`
  }
  if (abs >= 10_000) {
    return `R$ ${(amount / 1_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
  }

  return amount.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
}

export function formatNumber(value: number): string {
  return (value || 0).toLocaleString('pt-BR')
}

export function formatPercent(value: number, fractionDigits = 1): string {
  return `${(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: fractionDigits })}%`
}

export type DeltaTone = 'positive' | 'negative' | 'neutral'

export type Delta = {
  /** null when there is no comparable baseline */
  percent: number | null
  tone: DeltaTone
  label: string
}

/**
 * A percentage change needs a baseline to mean anything. When the previous period had
 * nothing, "+100%" would be noise, so we say "novo" instead of inventing a ratio.
 */
export function computeDelta(
  current: number,
  previous: number,
  lowerIsBetter = false,
): Delta {
  if (!previous) {
    if (!current) return { percent: null, tone: 'neutral', label: 'sem base anterior' }
    return {
      percent: null,
      tone: lowerIsBetter ? 'negative' : 'positive',
      label: 'novo no período',
    }
  }

  const percent = ((current - previous) / Math.abs(previous)) * 100
  const rounded = Math.round(percent * 10) / 10

  if (rounded === 0) return { percent: 0, tone: 'neutral', label: 'estável' }

  const improved = lowerIsBetter ? rounded < 0 : rounded > 0

  return {
    percent: rounded,
    tone: improved ? 'positive' : 'negative',
    label: `${rounded > 0 ? '+' : ''}${rounded.toLocaleString('pt-BR', {
      maximumFractionDigits: 1,
    })}%`,
  }
}

export function formatIdleDays(days: number | null): string | null {
  if (days === null || days === undefined) return null
  if (days < 1) return 'hoje'
  const rounded = Math.round(days)
  return rounded === 1 ? '1 dia' : `${rounded} dias`
}
